/**
 * "Recent vault sends" — kept only in this browser (D66). Public information only (network,
 * amount, recipient, transaction hash); the server stores nothing about vault sends.
 * Wrapped in try/catch: storage can be blocked, and then the list is simply empty.
 */
import type { NetworkMode } from "@custos/api-client";
import { useSyncExternalStore } from "react";

export interface VaultSendRecord {
  chain: string;
  asset: string;
  /** Human decimal string. */
  amount: string;
  to: string;
  txHash: string;
  /** ISO time. */
  at: string;
}

const MAX = 20;
const key = (mode: NetworkMode) => `custos.vaultSends.${mode}`;
const listeners = new Set<() => void>();
const cache = new Map<string, VaultSendRecord[]>();

function read(mode: NetworkMode): VaultSendRecord[] {
  const k = key(mode);
  if (!cache.has(k)) {
    let list: VaultSendRecord[] = [];
    try {
      const parsed: unknown = JSON.parse(localStorage.getItem(k) ?? "[]");
      if (Array.isArray(parsed)) list = parsed as VaultSendRecord[];
    } catch {
      list = [];
    }
    cache.set(k, list);
  }
  return cache.get(k)!;
}

export function addVaultSend(mode: NetworkMode, record: VaultSendRecord): void {
  const list = [record, ...read(mode).filter((r) => r.txHash !== record.txHash)].slice(0, MAX);
  cache.set(key(mode), list);
  try {
    localStorage.setItem(key(mode), JSON.stringify(list));
  } catch {
    // Not remembered — fine.
  }
  for (const l of listeners) l();
}

export function useVaultSends(mode: NetworkMode | undefined): VaultSendRecord[] {
  return useSyncExternalStore(
    (onChange) => {
      listeners.add(onChange);
      return () => listeners.delete(onChange);
    },
    () => (mode ? read(mode) : EMPTY),
  );
}

const EMPTY: VaultSendRecord[] = [];
